import { MagnifyingGlassIcon, QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { DiscoverablePage } from "@/types";

type Props = {
  open: boolean;
  onClose: () => void;
  pages: DiscoverablePage[];
  discovering?: boolean;
  canClose: boolean;
  title: string;
  selectedSummary: string;
  closeAriaLabel: string;
  checkboxAriaLabel: (page: DiscoverablePage) => string;
  selectedIds: Set<string>;
  probingById: Record<string, boolean>;
  onTogglePage: (pageId: string) => void;
  onProbingChange: (pageId: string, value: boolean) => void;
  needsSelection: boolean;
  hint: string;
  submitting: boolean;
  submitLabel: string;
  onConfirm: () => void;
  leading?: ReactNode;
};

const PROBING_TOOLTIP =
  "On = explore any interaction. Off = navigate/scroll/menus OK; skip signups, form submits, newsletters, orders, and similar side effects. Search & filters OK.";

export default function DomainPagePickerDialog({
  open,
  onClose,
  pages,
  discovering = false,
  canClose,
  title,
  selectedSummary,
  closeAriaLabel,
  checkboxAriaLabel,
  selectedIds,
  probingById,
  onTogglePage,
  onProbingChange,
  needsSelection,
  hint,
  submitting,
  submitLabel,
  onConfirm,
  leading,
}: Props) {
  const [query, setQuery] = useState("");
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !canClose) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, canClose, onClose]);

  if (!open || !overlayRoot) {
    return null;
  }

  const filteredPages = pages.filter((page) => {
    const haystack = `${page.title || ""} ${page.path}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 flex items-stretch justify-center overflow-hidden p-0 sm:items-center sm:overflow-y-auto sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      {canClose ? (
        <button
          type="button"
          aria-label={closeAriaLabel}
          className="absolute inset-0 hidden bg-gray-900/20 backdrop-blur-sm sm:block"
          onClick={onClose}
        />
      ) : (
        <div className="absolute inset-0 hidden bg-gray-900/20 backdrop-blur-sm sm:block" aria-hidden="true" />
      )}
      <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-lg sm:h-[min(40rem,calc(100dvh-2rem))] sm:rounded-lg">
        {discovering ? (
          <DiscoveringBody />
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <p className="mt-1 text-sm text-gray-600">{selectedSummary}</p>
              </div>
              {canClose ? (
                <button
                  type="button"
                  className="cursor-pointer text-gray-400 hover:text-gray-600"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {leading ? <div className="px-6 pt-3">{leading}</div> : null}
              <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-3">
                <label className="relative block">
                  <MagnifyingGlassIcon className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search pages"
                    className="input w-full pl-9"
                  />
                </label>
              </div>
              <div className="px-6 py-3">
                {filteredPages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500">No pages match your search.</p>
                ) : (
                  <>
                    <div className="mb-1 grid grid-cols-[1.25rem_1fr_auto] items-end gap-3 border-b border-gray-200 pb-2 text-[0.7rem] font-bold tracking-wide text-gray-500 uppercase">
                      <span />
                      <span>Page</span>
                      <span className="group relative inline-flex items-center justify-self-end gap-1">
                        Full probing
                        <button
                          type="button"
                          className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 bg-white text-[0.65rem] font-bold text-gray-600"
                          aria-label="About full probing"
                        >
                          <QuestionMarkCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <span className="pointer-events-none absolute top-full right-0 z-10 mt-1.5 hidden w-64 rounded bg-gray-900 px-3 py-2 text-xs leading-relaxed font-normal tracking-normal text-white normal-case shadow-lg group-hover:block group-focus-within:block">
                          {PROBING_TOOLTIP}
                        </span>
                      </span>
                    </div>
                    <ul>
                      {filteredPages.map((page) => (
                        <li
                          key={page.id}
                          className="grid grid-cols-[1.25rem_1fr_auto] items-start gap-3 border-b border-gray-100 py-3 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedIds.has(page.id)}
                            onChange={() => onTogglePage(page.id)}
                            aria-label={checkboxAriaLabel(page)}
                          />
                          <button
                            type="button"
                            className="cursor-pointer text-left"
                            onClick={() => onTogglePage(page.id)}
                          >
                            <span className="block font-medium text-gray-900">{page.title || page.path}</span>
                            <span className="block text-sm text-gray-500">{page.path}</span>
                          </button>
                          <div className="justify-self-end pt-0.5">
                            <input
                              type="checkbox"
                              checked={probingById[page.id] ?? false}
                              onChange={(event) => onProbingChange(page.id, event.target.checked)}
                              aria-label={`Full probing for ${page.title || page.path}`}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">{hint}</p>
              <div className="flex gap-3 sm:justify-end">
                {canClose ? (
                  <button type="button" className="btn max-sm:flex-1" onClick={onClose}>
                    Cancel
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-primary max-sm:flex-1"
                  disabled={needsSelection || submitting}
                  onClick={onConfirm}
                >
                  {submitLabel}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    overlayRoot,
  );
}

function DiscoveringBody() {
  return (
    <div className="px-6 py-10 text-center">
      <h2 className="text-lg font-semibold text-gray-900">Mapping your website…</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-gray-600">
        Finding pages on this domain so you can choose which ones we'll evaluate.
      </p>
      <div className="mx-auto mt-8 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <p className="mt-4 text-sm text-gray-500">This usually takes a minute.</p>
    </div>
  );
}

export function majorityProbing(pages: DiscoverablePage[]): boolean {
  const onCount = pages.filter((page) => page.full_application_probing).length;
  const offCount = pages.length - onCount;
  if (onCount === offCount) {
    return false;
  }
  return onCount > offCount;
}
